/**
 * ============================================================================
 * APP HEADER — the dark product identity bar (Cloudscape TopNavigation).
 * ============================================================================
 *
 * Product title on the left; the signed-in user on the right. The bar is
 * sticky above the AppLayout (see App.tsx headerSelector wiring), so identity
 * never scrolls away. Dashboard switching lives in the page-tabs strip
 * (DashboardSwitcher), not here.
 *
 * - Title links home through react-router (preventDefault + navigate), so
 *   the Mons basename keeps working. The home slug is the ACTIVE dashboard's
 *   canonical slug, so the logo returns to the dashboard the user is in.
 * - User utility shows currentUserAlias() — the same Mons-injected Midway
 *   identity the callout `actor` uses, so the name in the bar is exactly the
 *   author name a published callout will carry. Display-only (a button with
 *   no action, NOT a dropdown — there is nothing to show in a menu): the
 *   badge photo (Badge Photo Service; serves its own placeholder when a
 *   photo is missing) plus the alias. Outside a Mons session (standalone dev
 *   server) there is no real alias to fetch a photo for, so it falls back to
 *   the generic user icon with FALLBACK_ACTOR.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation, {
  TopNavigationProps,
} from '@amzn/awsui-components-core/top-navigation';
import { currentUserAlias, FALLBACK_ACTOR } from 'src/accessors/currentUser';
import { useAppSelector } from 'src/app/store';
import { toDashboardSlug } from 'src/config/dashboards';

export const APP_TITLE = 'KFE Business Reporting';

const I18N_STRINGS: TopNavigationProps.I18nStrings = {
  overflowMenuTriggerText: 'More',
  overflowMenuTitleText: 'All',
  overflowMenuBackIconAriaLabel: 'Back',
  overflowMenuDismissIconAriaLabel: 'Close menu',
};

export const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const alias = currentUserAlias();
  const isRealSession = alias !== FALLBACK_ACTOR;

  const activeDashboardId = useAppSelector(
    (state) => state.dashboard.activeDashboardId,
  );

  const utilities: TopNavigationProps.Utility[] = [
    {
      type: 'button',
      text: alias,
      ariaLabel: `Signed in as ${alias}`,
      disableTextCollapse: true,
      // Badge photo for the real signed-in alias; the generic icon locally
      // where FALLBACK_ACTOR has no badge.
      ...(isRealSession
        ? {
            iconUrl: `https://badgephotos.corp.amazon.com/?uid=${encodeURIComponent(
              alias,
            )}`,
            iconAlt: `${alias} badge photo`,
          }
        : { iconName: 'user-profile' as const }),
    },
  ];

  // Home slug follows the active dashboard so the logo returns to where the
  // user is (toDashboardSlug defaults the review type to WBR).
  const activeSlug = toDashboardSlug(activeDashboardId);

  return (
    <TopNavigation
      identity={{
        title: APP_TITLE,
        href: `/${activeSlug}`,
        onFollow: (event) => {
          event.preventDefault();
          navigate(`/${activeSlug}`);
        },
      }}
      utilities={utilities}
      i18nStrings={I18N_STRINGS}
    />
  );
};
